import React, { CSSProperties, useCallback } from 'react';
import { Tooltip } from 'antd';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { supportedMetricsSelector, intervalSelector } from '@store/common/selectors';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import {
  getAppEventsPreset,
  getServerSidePerformancePreset,
} from '../Dashboard/UseCases/configs/eco';
import { getValidateDimensionFunc, getValidateMetricFunc } from '../Dashboard/UseCases/utils';
import { drilldownDashboardType } from '../Dashboard/UseCases';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { isAppModule } from '@utils/constants';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { initialFilter } from '@src/store/common/reducer';
import { createFilterRule } from '@utils/filter';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import {
  NetworkWithBgIcon,
  EventWithBgIcon,
  EventsDashIcon,
  ServicePerformanceDashIcon,
  dimensionToCategory,
  dimensionByCategory,
} from './constants';

const DimensionTableDrilldownIconStyle: CSSProperties = {
  display: 'block',
  width: '38px',
  height: '33px',
  cursor: 'pointer',
};

export function useDrilldownIconOnDimensionTable(name: string) {
  const handleAction = useDrillDownToPreset(name);
  const { useCaseKey } = useDrilldownMeta();

  /** Only show Drilldown Icon on DPI Trends page */
  if (!isAppModule || useCaseKey) {
    return null;
  }

  const category = dimensionToCategory[name];

  if (category === 'events') {
    return (
      <Tooltip title={'Go to Events view'}>
        <EventWithBgIcon style={DimensionTableDrilldownIconStyle} onClick={handleAction} />
      </Tooltip>
    );
  }

  if (category === 'network') {
    return (
      <Tooltip title={'Go to Service Performance view'}>
        <NetworkWithBgIcon style={DimensionTableDrilldownIconStyle} onClick={handleAction} />
      </Tooltip>
    );
  }

  return null;
}

const DashIconStyle: CSSProperties = {
  display: 'block',
  width: '113px',
  height: '29px',
  position: 'relative',
  top: '1px',
};
export function useDrilldownIconOnDimensionPanel(category: string) {
  /** Only show Drilldown Icon on DPI Trends page */
  if (!isAppModule) {
    return null;
  }

  if (category === 'events') {
    return <EventsDashIcon style={DashIconStyle} />;
  }

  if (category === 'network') {
    return <ServicePerformanceDashIcon style={DashIconStyle} />;
  }

  return null;
}

const getPresetConfigFunc = (category: string) => {
  return {
    events: getAppEventsPreset,
    network: getServerSidePerformancePreset,
  }[category];
};

export function useDrillDownToPreset(dimensionOrCategory: string, rowData?: any) {
  const metrics = useSelector(supportedMetricsSelector) as object;
  const dimensionList = useSelector(allDimensionsListSelector) as [];
  const formattedInterval = useSelector(intervalSelector);
  const dispatch = useDispatch();

  const handleActions = useCallback(() => {
    let category = '';
    if (Object.prototype.hasOwnProperty.call(dimensionByCategory, dimensionOrCategory)) {
      category = dimensionOrCategory;
    } else {
      category = dimensionToCategory[dimensionOrCategory];
    }
    const getPresetConfig = getPresetConfigFunc(category);
    if (!getPresetConfig) {
      return;
    }
    const { config, title, key } = getPresetConfig({
      validateDimension: getValidateDimensionFunc(dimensionList),
      validateMetric: getValidateMetricFunc(metrics),
    });
    const { common = {}, ...restConfig } = config;
    const dashBoardConfig = {
      ...restConfig,
      common: {
        ...common,
        interval: formattedInterval,
      },
      summary: {},
      usecaseKey: key,
    };

    if (rowData) {
      const dimensionInfo = (dimensionList as Record<string, any>[]).find(
        (it) => it.groupBy === dimensionOrCategory,
      );
      const filterByValue = createFilterRule(
        dimensionInfo,
        get(rowData, 'name[0]', ''),
        get(rowData, 'value[0]', ''),
      );

      /**
       * The logic of handling drilldown and globalFilter is referred from function dimensionDrillown
       * in file `src/store/trends/base/saga.ts` to ensure the structure is consistent.
       */
      const combinedFilter = [[filterByValue]];
      const newRules = getRulesFromFilter(combinedFilter, true);
      const newFilter = getFilterFromRules(newRules, true);
      const drilldownFilters = [initialFilter, newFilter];

      dashBoardConfig.common['appliedFilters'] = [newFilter];
      dashBoardConfig.summary['drilldownData'] = drilldownFilters;
    }
    dispatch(createTempDashboard(drilldownDashboardType, dashBoardConfig, title));
  }, [dimensionOrCategory, metrics, formattedInterval, rowData, dispatch, dimensionList]);
  return handleActions;
}
